import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Alert,
  AlertIcon,
  Button,
  HStack,
  Input,
  Select,
  Text,
  Badge,
  Avatar,
  Flex,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  SimpleGrid,
} from '@chakra-ui/react';
import { FiUsers, FiUserCheck, FiAward, FiTrendingUp } from 'react-icons/fi';
import { apiService } from '../services/apiService';
import { getPhotoUrl } from '../config/image';
import { MemberBadge, BadgeRibbon } from '../components/MemberBadge';

export default function Members() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    groupe_id: '',
    type_membre: ''
  });
  
  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Récupérer les statistiques
  const fetchStatistics = async () => {
    try {
      setStatsLoading(true);
      const response = await apiService.makeRequest('/members/stats/overview');
      setStatistics(response);
    } catch (err) {
      console.error('Error fetching statistics:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  // Récupérer les groupes
  const fetchGroups = async () => {
    try {
      const response = await apiService.makeRequest('/members/meta/groups-and-functions');
      setGroups(response.groups || []);
    } catch (err) {
      console.error('Error fetching groups:', err);
    }
  };

  const fetchMembers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(filters.search && { search: filters.search }),
        ...(filters.groupe_id && { groupe_id: filters.groupe_id }),
        ...(filters.type_membre && { type_membre: filters.type_membre })
      });
      
      const response = await apiService.makeRequest(`/members?${params}`);
      
      setMembers(response.members);
      setPagination(prev => ({
        ...prev,
        ...response.pagination
      }));
      
    } catch (err) {
      console.error('Error fetching members:', err);
      setError(err.message || 'Erreur lors du chargement des membres');
      toast({
        title: 'Erreur',
        description: err.message || 'Erreur lors du chargement des membres',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatistics();
    fetchGroups();
  }, []);

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, filters]);

  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleViewMember = (member) => {
    setSelectedMember(member);
    onOpen();
  };

  const handleEditMember = (memberId) => {
    navigate(`/members/${memberId}/edit`);
  };

  const handleAddMember = () => {
    navigate('/members/new');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  if (loading && members.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
        <Spinner size="xl" color="blue.500" />
      </Box>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg" color="gray.800">
          Gestion des Membres
        </Heading>
        <Button colorScheme="blue" onClick={handleAddMember} size="lg" shadow="md">
          + Ajouter un membre
        </Button>
      </Flex>

      {/* Badges de statistiques - Version élégante */}
      {!statsLoading && statistics && (
        <>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
            {/* Badge total avec effet gradient */}
            <MemberBadge
              icon={FiUsers}
              label="Total Membres"
              value={statistics.total}
              color="blue"
              variant="gradient"
            />
            
            {/* Badges par type (3 premiers) avec variant outline */}
            {statistics.by_type && statistics.by_type.length > 0 && (
              <>
                {statistics.by_type.slice(0, 3).map((type, index) => {
                  const colors = ["green", "purple", "orange"];
                  return (
                    <MemberBadge
                      key={type.type_membre}
                      icon={FiUserCheck}
                      label={type.type_membre}
                      value={type.count}
                      color={colors[index] || "blue"}
                      variant="outline"
                    />
                  );
                })}
              </>
            )}
          </SimpleGrid>


          {/* Liste complète des groupes en badges compacts */}
          {statistics.by_group && statistics.by_group.length > 0 && (
            <Box bg="white" p={4} borderRadius="md" shadow="sm" mb={6}>
              <Text fontSize="md" fontWeight="bold" mb={3} color="gray.700">
                Répartition complète par groupe
              </Text>
              <Flex wrap="wrap" gap={2}>
                {statistics.by_group.map((group) => (
                  <Badge
                    key={group.id}
                    colorScheme="teal"
                    fontSize="sm"
                    px={4}
                    py={2}
                    borderRadius="full"
                    fontWeight="semibold"
                    shadow="sm"
                  >
                    {group.nom}: {group.count} membres
                  </Badge>
                ))}
              </Flex>
            </Box>
          )}
        </>
      )}

      {/* Filtres de recherche */}
      <Box bg="white" p={4} borderRadius="md" shadow="sm" mb={6}>
        <HStack spacing={4}>
          <Box flex={1}>
            <Input
              placeholder="🔍 Rechercher par nom, prénom, email ou matricule..."
              value={filters.search}
              onChange={(e) => handleSearch(e.target.value)}
              size="lg"
            />
          </Box>
          <Select
            placeholder="Tous les groupes"
            value={filters.groupe_id}
            onChange={(e) => setFilters(prev => ({ ...prev, groupe_id: e.target.value }))}
            w="250px"
            size="lg"
          >
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.nom} ({group.membre_count})
              </option>
            ))}
          </Select>
          <Select
            placeholder="Tous les types"
            value={filters.type_membre}
            onChange={(e) => setFilters(prev => ({ ...prev, type_membre: e.target.value }))}
            w="250px"
            size="lg"
          >
            <option value="Communiant">Communiant</option>
            <option value="Non communiant">Non communiant</option>
          </Select>
        </HStack>
      </Box>

      {error && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* Table des membres */}
      <Box bg="white" borderRadius="md" shadow="sm" overflow="hidden">
        <TableContainer>
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Photo</Th>
                <Th>Matricule</Th>
                <Th>N° Carte</Th>
                <Th>Nom</Th>
                <Th>Prénom</Th>
                <Th>Email</Th>
                <Th>Téléphone</Th>
                <Th>Date d'adhésion</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {members.length === 0 ? (
                <Tr>
                  <Td colSpan={9} textAlign="center" py={10}>
                    <Text color="gray.500">Aucun membre trouvé</Text>
                  </Td>
                </Tr>
              ) : (
                members.map((member) => (
                  <Tr key={member.id} _hover={{ bg: member.is_active ? 'gray.50' : 'red.50' }} bg={member.is_active ? 'white' : 'red.25'} opacity={member.is_active ? 1 : 0.7}>
                    <Td>
                      <Avatar
                        size="sm"
                        name={`${member.prenom} ${member.nom}`}
                        src={member.photo ? getPhotoUrl(member.photo) : undefined}
                      />
                    </Td>
                    <Td>
                      <Badge colorScheme="blue">{member.matricule}</Badge>
                    </Td>
                    <Td>
                      <Badge colorScheme="purple">{member.numero_carte || '-'}</Badge>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Text fontWeight="medium">{member.nom}</Text>
                        {!member.is_active && <Badge colorScheme="red" fontSize="xs">Inactif</Badge>}
                      </HStack>
                    </Td>
                    <Td>{member.prenom}</Td>
                    <Td>{member.email || '-'}</Td>
                    <Td>{member.telephone || '-'}</Td>
                    <Td>{formatDate(member.date_adhesion)}</Td>
                    <Td>
                      <HStack spacing={2}>
                        <Button 
                          size="sm" 
                          colorScheme="blue" 
                          variant="ghost"
                          onClick={() => handleViewMember(member)}
                        >
                          Voir
                        </Button>
                        <Button 
                          size="sm" 
                          colorScheme="gray" 
                          variant="ghost"
                          onClick={() => handleEditMember(member.id)}
                        >
                          Modifier
                        </Button>
                      </HStack>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </TableContainer>

        {pagination.pages > 1 && (
          <Flex justify="space-between" align="center" p={4} borderTop="1px" borderColor="gray.200">
            <Text fontSize="sm" color="gray.600">
              Page {pagination.page} sur {pagination.pages} ({pagination.total} membres)
            </Text>
            <HStack>
              <Button
                size="sm"
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                isDisabled={pagination.page === 1}
              >
                Précédent
              </Button>
              <Button
                size="sm"
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                isDisabled={pagination.page === pagination.pages}
              >
                Suivant
              </Button>
            </HStack>
          </Flex>
        )}
      </Box>

      {/* Modal de détails du membre */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Détails du membre</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedMember && (
              <Box>
                <Flex align="center" mb={4}>
                  <Avatar
                    size="xl"
                    name={`${selectedMember.prenom} ${selectedMember.nom}`}
                    src={selectedMember.photo ? getPhotoUrl(selectedMember.photo) : undefined}
                    mr={4}
                  />
                  <Box>
                    <Text fontSize="xl" fontWeight="bold">
                      {selectedMember.prenom} {selectedMember.nom}
                    </Text>
                    <HStack spacing={2} mt={1}>
                      <Badge colorScheme="blue">{selectedMember.matricule}</Badge>
                      {selectedMember.numero_carte && (
                        <Badge colorScheme="purple">{selectedMember.numero_carte}</Badge>
                      )}
                    </HStack>
                  </Box>
                </Flex>
                
                <Box>
                  <Text mb={2}><strong>Email:</strong> {selectedMember.email || '-'}</Text>
                  <Text mb={2}><strong>Téléphone:</strong> {selectedMember.telephone || '-'}</Text>
                  <Text mb={2}><strong>Date d'adhésion:</strong> {formatDate(selectedMember.date_adhesion)}</Text>
                  {selectedMember.adresse && (
                    <Text mb={2}><strong>Adresse:</strong> {selectedMember.adresse}</Text>
                  )}
                  {selectedMember.ville && (
                    <Text mb={2}><strong>Ville:</strong> {selectedMember.ville}</Text>
                  )}
                </Box>

                <Flex mt={6} justify="flex-end">
                  <Button 
                    colorScheme="blue" 
                    onClick={() => {
                      onClose();
                      handleEditMember(selectedMember.id);
                    }}
                  >
                    Modifier
                  </Button>
                </Flex>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}